import React, { useState } from 'react';
import { BlockoutRule, BlockoutType, CapacityRule, SpecialOpening, Venue } from '@/types';
import { useVenueMutations } from '@features/admin/data';
import { Calendar, Clock, MapPin, Trash2, X } from 'lucide-react';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { getGoogleMapsUrl, getVenueMapUrl, hasValidCoordinates } from '@/lib/venueMaps';
import { formatUkDate } from '@/lib/dates';
import { normalizeSlotIntervalMinutes } from '@/lib/scheduling';
import { createId } from '@/lib/ids';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import {
  DAYS,
  DEFAULT_SCHEDULE,
  DayScheduleField,
  DayScheduleValue,
  buildConflictMessage,
  findBlockoutSpecialOpeningConflict,
  getScheduleWithDefaults,
  isVenueRuleFrequency,
  normalizeDayCapacity,
  parseCoordinatesDraft,
  rangesOverlap,
  timeToMinutes,
  VenueRuleFrequency
} from './venueUtils';

// --- VenueMapPreview ---

const VenueMapPreview = ({ venue }: { venue: Venue }) => {
  const mapUrl = getVenueMapUrl(venue, { width: 360, height: 220, zoom: 15 });
  const mapsLink = getGoogleMapsUrl(venue);
  const hasAddress = Boolean(venue.address || venue.city);
  const hasCoordinates = hasValidCoordinates(venue.coordinates);
  const statusMessage = hasCoordinates
    ? 'Preview uses current coordinates. Save changes to persist.'
    : hasAddress
      ? 'Save venue to generate a map preview.'
      : 'Enter address and city to generate a preview.';

  return (
    <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
      <div className="w-28 h-20 border rounded-lg bg-white flex items-center justify-center overflow-hidden shadow-inner shrink-0">
        {mapUrl ? (
          mapsLink ? (
            <a
              href={mapsLink}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open ${venue.name} in Google Maps`}
              className="block w-full h-full"
            >
              <img src={mapUrl} alt="Map preview" className="w-full h-full object-cover" />
            </a>
          ) : (
            <img src={mapUrl} alt="Map preview" className="w-full h-full object-cover" />
          )
        ) : (
          <div className="bg-slate-100 w-full h-full flex items-center justify-center">
            <MapPin className="w-6 h-6 text-slate-300" />
          </div>
        )}
      </div>
      <div className="flex-1">
        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tight">
          OpenStreetMap Preview
        </p>
        <p className="text-[9px] text-slate-400 leading-tight mt-1">{statusMessage}</p>
      </div>
    </div>
  );
};

// --- VenueEditor ---

interface VenueEditorProps {
  venue: Venue;
  initialTab?: 'details' | 'schedule';
  onClose: () => void;
  onSaved: () => void;
}

export const VenueEditor: React.FC<VenueEditorProps> = ({
  venue: initialVenue,
  initialTab = 'details',
  onClose,
  onSaved
}) => {
  const venueMutations = useVenueMutations();
  const [venue, setVenue] = useState<Venue>(initialVenue);
  const [activeTab, setActiveTab] = useState<'details' | 'schedule'>(initialTab);
  const [isSaving, setIsSaving] = useState(false);
  const [coordinatesDraft, setCoordinatesDraft] = useState({
    lat: initialVenue.coordinates?.lat != null ? String(initialVenue.coordinates.lat) : '',
    lng: initialVenue.coordinates?.lng != null ? String(initialVenue.coordinates.lng) : ''
  });

  const [newRuleType, setNewRuleType] = useState<BlockoutType>('single');
  const [newRuleData, setNewRuleData] = useState<Partial<BlockoutRule>>({});
  const [newCapacityRule, setNewCapacityRule] = useState<Partial<CapacityRule>>({
    dayOfWeek: 1,
    start: '09:00',
    end: '12:00',
    capacity: 1,
    slotIntervalMinutes: undefined
  });
  const [newSpecialOpening, setNewSpecialOpening] = useState<Partial<SpecialOpening>>({
    date: '',
    start: '09:00',
    end: '17:00',
    capacity: undefined,
    label: ''
  });

  const currentSchedule = () => getScheduleWithDefaults(venue.schedule);

  const updateDaySchedule = (
    dayIndex: number,
    field: DayScheduleField,
    value: DayScheduleValue
  ) => {
    const sched = currentSchedule();
    const nextValue =
      field === 'capacity'
        ? normalizeDayCapacity(typeof value === 'number' ? value : Number(value))
        : value;
    setVenue({
      ...venue,
      schedule: {
        ...sched,
        weekly: { ...sched.weekly, [dayIndex]: { ...sched.weekly[dayIndex], [field]: nextValue } }
      }
    });
  };

  const updateSlotIntervalMinutes = (value: number) => {
    const sched = currentSchedule();
    setVenue({
      ...venue,
      schedule: { ...sched, slotIntervalMinutes: normalizeSlotIntervalMinutes(value) }
    });
  };

  const handleAddBlockoutRule = async () => {
    if (newRuleType === 'single' && !newRuleData.date) return showAlertModal('Select a date');
    if (newRuleType === 'range' && (!newRuleData.startDate || !newRuleData.endDate))
      return showAlertModal('Select start and end dates');
    if (
      newRuleType === 'pattern' &&
      (newRuleData.dayOfWeek === undefined ||
        !newRuleData.frequency ||
        (newRuleData.frequency === 'biweekly' && !newRuleData.startFrom))
    )
      return showAlertModal('Complete pattern details');

    const newRule: BlockoutRule = {
      id: createId(),
      type: newRuleType,
      reason: newRuleData.reason || 'Closed',
      ...newRuleData
    };
    const sched = currentSchedule();
    const conflict = findBlockoutSpecialOpeningConflict(sched, { kind: 'blockout', rule: newRule });
    if (conflict) {
      const proceed = await showConfirmModal(buildConflictMessage('addingBlockout', conflict), {
        title: 'Schedule conflict',
        confirmText: 'Add anyway',
        cancelText: 'Cancel'
      });
      if (!proceed) return;
    }
    setVenue({ ...venue, schedule: { ...sched, blockouts: [...sched.blockouts, newRule] } });
    setNewRuleData({});
  };

  const removeBlockoutRule = (ruleId: string) => {
    const sched = currentSchedule();
    setVenue({
      ...venue,
      schedule: { ...sched, blockouts: sched.blockouts.filter((r) => r.id !== ruleId) }
    });
  };

  const addCapacityRule = () => {
    const dayOfWeek = Number(newCapacityRule.dayOfWeek);
    const start = String(newCapacityRule.start || '');
    const end = String(newCapacityRule.end || '');
    const capacity = Number(newCapacityRule.capacity);
    const rawSlotInterval = newCapacityRule.slotIntervalMinutes;
    const parsedSlotInterval = rawSlotInterval == null ? null : Number(rawSlotInterval);

    if (!Number.isInteger(dayOfWeek) || dayOfWeek < 0 || dayOfWeek > 6)
      return showAlertModal('Select a valid day for the capacity block.');
    if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end))
      return showAlertModal('Select valid start and end times for the capacity block.');
    if (end <= start)
      return showAlertModal('Capacity block end time must be later than start time.');
    if (!Number.isFinite(capacity) || capacity < 0 || capacity > 50)
      return showAlertModal('Capacity must be between 0 and 50.');
    if (
      parsedSlotInterval != null &&
      (!Number.isFinite(parsedSlotInterval) || parsedSlotInterval < 5 || parsedSlotInterval > 180)
    )
      return showAlertModal('Slot length must be between 5 and 180 minutes.');

    const sched = currentSchedule();
    if (parsedSlotInterval != null) {
      const overlapping = (sched.capacityRules || []).find(
        (r) =>
          r.dayOfWeek === dayOfWeek &&
          r.slotIntervalMinutes != null &&
          rangesOverlap(start, end, r.start, r.end)
      );
      if (overlapping)
        return showAlertModal(
          'Slot length overrides cannot overlap. Adjust the time range or remove the other slot-length rule.'
        );
    }

    const newRule: CapacityRule = {
      id: createId(),
      dayOfWeek,
      start,
      end,
      capacity: Math.floor(capacity),
      label: String(newCapacityRule.label || '').trim() || undefined,
      slotIntervalMinutes:
        parsedSlotInterval != null ? normalizeSlotIntervalMinutes(parsedSlotInterval) : undefined
    };
    setVenue({
      ...venue,
      schedule: { ...sched, capacityRules: [...(sched.capacityRules || []), newRule] }
    });
    setNewCapacityRule({
      dayOfWeek,
      start: '09:00',
      end: '12:00',
      capacity: 1,
      label: '',
      slotIntervalMinutes: undefined
    });
  };

  const removeCapacityRule = (ruleId: string) => {
    const sched = currentSchedule();
    setVenue({
      ...venue,
      schedule: {
        ...sched,
        capacityRules: (sched.capacityRules || []).filter((r) => r.id !== ruleId)
      }
    });
  };

  const addSpecialOpening = async () => {
    const date = String(newSpecialOpening.date || '');
    const start = String(newSpecialOpening.start || '');
    const end = String(newSpecialOpening.end || '');
    const capacity = newSpecialOpening.capacity == null ? null : Number(newSpecialOpening.capacity);

    if (!date) return showAlertModal('Select a date for the special opening.');
    if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end))
      return showAlertModal('Select valid start and end times.');
    const startMin = timeToMinutes(start);
    const endMin = timeToMinutes(end);
    if (startMin == null || endMin == null || endMin <= startMin)
      return showAlertModal('Special opening end time must be later than start time.');
    if (capacity != null && (!Number.isFinite(capacity) || capacity < 0 || capacity > 50))
      return showAlertModal('Capacity must be between 0 and 50.');

    const sched = currentSchedule();
    if ((sched.specialOpenings || []).find((o) => o.date === date))
      return showAlertModal('A special opening already exists for this date.');

    const newOpening: SpecialOpening = {
      id: createId(),
      date,
      start,
      end,
      capacity: capacity == null ? undefined : Math.floor(capacity),
      label: String(newSpecialOpening.label || '').trim() || undefined
    };
    const conflict = findBlockoutSpecialOpeningConflict(sched, {
      kind: 'specialOpening',
      opening: newOpening
    });
    if (conflict) {
      const proceed = await showConfirmModal(
        buildConflictMessage('addingSpecialOpening', conflict),
        { title: 'Schedule conflict', confirmText: 'Add anyway', cancelText: 'Cancel' }
      );
      if (!proceed) return;
    }
    setVenue({
      ...venue,
      schedule: { ...sched, specialOpenings: [...(sched.specialOpenings || []), newOpening] }
    });
    setNewSpecialOpening({
      date: '',
      start: '09:00',
      end: '17:00',
      capacity: undefined,
      label: ''
    });
  };

  const removeSpecialOpening = (openingId: string) => {
    const sched = currentSchedule();
    setVenue({
      ...venue,
      schedule: {
        ...sched,
        specialOpenings: (sched.specialOpenings || []).filter((o) => o.id !== openingId)
      }
    });
  };

  const handleSave = async () => {
    const parsedCoordinates = parseCoordinatesDraft(coordinatesDraft);
    if (parsedCoordinates.hasValue && !parsedCoordinates.isValid) {
      return showAlertModal(
        'Coordinates must include a valid latitude (-90 to 90) and longitude (-180 to 180).'
      );
    }
    const venueToSave = { ...venue, schedule: getScheduleWithDefaults(venue.schedule) };
    if (parsedCoordinates.coords) {
      venueToSave.coordinates = parsedCoordinates.coords;
    } else {
      delete venueToSave.coordinates;
    }
    setIsSaving(true);
    try {
      if (venueToSave.id) {
        await venueMutations.update(venueToSave);
      } else {
        await venueMutations.create({ ...venueToSave, id: createId() });
      }
      onSaved();
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to save venue. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  const sched = getScheduleWithDefaults(venue.schedule);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-fade-in">
        <div className="p-4 border-b bg-slate-50 rounded-t-xl flex justify-between items-center">
          <h3 className="text-lg font-bold text-slate-800">
            {venue.id ? 'Edit Venue' : 'Add New Venue'}
          </h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-slate-400 hover:text-slate-600" />
          </button>
        </div>

        <div className="flex border-b bg-white">
          {(['details', 'schedule'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-bold uppercase tracking-wider transition-all ${activeTab === tab ? 'text-teal-600 border-b-2 border-teal-600' : 'text-slate-400 hover:text-slate-600'}`}
            >
              {tab === 'details' ? 'Venue Details' : 'Opening Hours & Blockouts'}
            </button>
          ))}
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === 'details' ? (
            <div className="space-y-5">
              {(['name', 'address', 'city'] as const).map((field) => (
                <div key={field}>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">
                    {field.charAt(0).toUpperCase() + field.slice(1)}
                  </label>
                  <input
                    className="w-full border p-2.5 rounded-lg bg-slate-50 focus:ring-2 focus:ring-teal-500 outline-none"
                    placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
                    value={venue[field]}
                    onChange={(e) => setVenue({ ...venue, [field]: e.target.value })}
                  />
                </div>
              ))}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">
                  Coordinates (Optional)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(['lat', 'lng'] as const).map((coord) => (
                    <input
                      key={coord}
                      type="number"
                      step="0.000001"
                      inputMode="decimal"
                      className="w-full border p-2.5 rounded-lg bg-slate-50 focus:ring-2 focus:ring-teal-500 outline-none"
                      placeholder={coord === 'lat' ? 'Latitude' : 'Longitude'}
                      value={coordinatesDraft[coord]}
                      onChange={(e) =>
                        setCoordinatesDraft((prev) => ({ ...prev, [coord]: e.target.value }))
                      }
                    />
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Leave blank to auto-detect from address. Enter both values to override.
                </p>
                {(() => {
                  const parsed = parseCoordinatesDraft(coordinatesDraft);
                  if (!parsed.hasValue || parsed.isValid) return null;
                  return (
                    <p className="text-[11px] text-red-500 mt-1">
                      Enter a valid latitude (-90 to 90) and longitude (-180 to 180).
                    </p>
                  );
                })()}
              </div>
              {(() => {
                const parsed = parseCoordinatesDraft(coordinatesDraft);
                const previewVenue =
                  parsed.isValid && parsed.coords
                    ? { ...venue, coordinates: parsed.coords }
                    : venue;
                return <VenueMapPreview venue={previewVenue} />;
              })()}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Slot Interval */}
              <div>
                <h4 className="font-bold text-sm text-slate-800 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-indigo-500" /> Appointment Slot Interval
                </h4>
                <div className="bg-slate-50 border rounded-xl p-4 shadow-inner">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                        Slot Length
                      </p>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Controls booking start times for this location.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={5}
                        max={180}
                        step={5}
                        className="w-24 border rounded-lg px-2 py-1.5 text-sm font-bold bg-white outline-none focus:ring-2 focus:ring-teal-500"
                        value={sched.slotIntervalMinutes}
                        onChange={(e) => updateSlotIntervalMinutes(Number(e.target.value || 30))}
                      />
                      <span className="text-xs font-bold text-slate-500 uppercase">mins</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Weekly Hours */}
              <div>
                <h4 className="font-bold text-sm text-slate-800 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-teal-600" /> Standard Weekly Hours
                </h4>
                <div className="bg-slate-50 border rounded-xl overflow-hidden shadow-inner">
                  {DAYS.map((day, idx) => {
                    const daySchedule = sched.weekly[idx] || {
                      open: false,
                      start: '09:00',
                      end: '17:00'
                    };
                    return (
                      <div
                        key={day}
                        className="flex items-center justify-between p-3 border-b last:border-0 hover:bg-white transition-colors"
                      >
                        <div className="w-24 text-sm font-bold text-slate-700">{day}</div>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="w-4 h-4 text-teal-600 rounded"
                              checked={daySchedule.open}
                              onChange={(e) => updateDaySchedule(idx, 'open', e.target.checked)}
                            />
                            <span
                              className={`text-[10px] font-bold tracking-widest ${daySchedule.open ? 'text-green-600' : 'text-slate-400'}`}
                            >
                              {daySchedule.open ? 'OPEN' : 'CLOSED'}
                            </span>
                          </label>
                          {daySchedule.open && (
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2 text-sm bg-white border rounded-lg px-2 py-1 shadow-sm">
                                <input
                                  type="time"
                                  className="border-none p-0 text-xs font-medium outline-none"
                                  value={daySchedule.start}
                                  onChange={(e) => updateDaySchedule(idx, 'start', e.target.value)}
                                />
                                <span className="text-slate-300">-</span>
                                <input
                                  type="time"
                                  className="border-none p-0 text-xs font-medium outline-none"
                                  value={daySchedule.end}
                                  onChange={(e) => updateDaySchedule(idx, 'end', e.target.value)}
                                />
                              </div>
                              <div className="flex items-center gap-2 bg-white border rounded-lg px-2 py-1 shadow-sm">
                                <span className="text-[10px] font-bold text-slate-400 uppercase">
                                  Cap
                                </span>
                                <input
                                  type="number"
                                  min={0}
                                  max={50}
                                  className="w-14 border-none p-0 text-xs font-medium outline-none"
                                  value={daySchedule.capacity ?? 1}
                                  onChange={(e) =>
                                    updateDaySchedule(idx, 'capacity', e.target.value)
                                  }
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Capacity Blocks */}
              <div>
                <h4 className="font-bold text-sm text-slate-800 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" /> Concurrent Capacity Blocks
                </h4>
                <div className="bg-slate-50 border rounded-xl p-4 mb-4 space-y-3 shadow-inner">
                  <p className="text-[11px] text-slate-500">
                    Set how many appointments can run at once in a time range. Use{' '}
                    <strong>0</strong> for breaks (for example lunch 12:00-13:00).
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <select
                      className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                      value={newCapacityRule.dayOfWeek ?? 1}
                      onChange={(e) =>
                        setNewCapacityRule((prev) => ({
                          ...prev,
                          dayOfWeek: Number(e.target.value)
                        }))
                      }
                    >
                      {DAYS.map((day, idx) => (
                        <option key={day} value={idx}>
                          {day}
                        </option>
                      ))}
                    </select>
                    <input
                      className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Label (optional)"
                      value={newCapacityRule.label || ''}
                      onChange={(e) =>
                        setNewCapacityRule((prev) => ({ ...prev, label: e.target.value }))
                      }
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                    <input
                      type="time"
                      className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                      value={newCapacityRule.start || '09:00'}
                      onChange={(e) =>
                        setNewCapacityRule((prev) => ({ ...prev, start: e.target.value }))
                      }
                    />
                    <input
                      type="time"
                      className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                      value={newCapacityRule.end || '12:00'}
                      onChange={(e) =>
                        setNewCapacityRule((prev) => ({ ...prev, end: e.target.value }))
                      }
                    />
                    <input
                      type="number"
                      min={0}
                      max={50}
                      className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                      value={newCapacityRule.capacity ?? 1}
                      onChange={(e) =>
                        setNewCapacityRule((prev) => ({
                          ...prev,
                          capacity: Number(e.target.value || 0)
                        }))
                      }
                    />
                    <input
                      type="number"
                      min={5}
                      max={180}
                      step={5}
                      className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Slot mins"
                      value={newCapacityRule.slotIntervalMinutes ?? ''}
                      onChange={(e) =>
                        setNewCapacityRule((prev) => ({
                          ...prev,
                          slotIntervalMinutes:
                            e.target.value === '' ? undefined : Number(e.target.value)
                        }))
                      }
                    />
                    <button
                      onClick={addCapacityRule}
                      className="bg-teal-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-teal-700 shadow-sm transition-all"
                    >
                      Add Block
                    </button>
                  </div>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                  {(venue.schedule?.capacityRules || []).length === 0 && (
                    <p className="text-xs text-slate-400 italic text-center py-4 bg-white rounded-xl border border-dashed">
                      No capacity blocks configured.
                    </p>
                  )}
                  {(venue.schedule?.capacityRules || [])
                    .slice()
                    .sort((a, b) =>
                      a.dayOfWeek !== b.dayOfWeek
                        ? a.dayOfWeek - b.dayOfWeek
                        : a.start.localeCompare(b.start)
                    )
                    .map((rule) => (
                      <div
                        key={rule.id}
                        className="flex justify-between items-center bg-white border border-slate-200 p-3 rounded-xl shadow-sm group hover:border-teal-300 transition-all"
                      >
                        <div>
                          <div className="font-bold text-slate-800 text-sm">
                            {DAYS[rule.dayOfWeek]} {rule.start}-{rule.end}
                          </div>
                          <div className="text-[10px] font-bold text-slate-400 uppercase mt-0.5 tracking-tight">
                            {rule.capacity === 0
                              ? `Break ${rule.label ? `- ${rule.label}` : ''}`
                              : `${rule.capacity} concurrent slot${rule.capacity === 1 ? '' : 's'}${rule.label ? ` - ${rule.label}` : ''}${rule.slotIntervalMinutes ? ` · ${rule.slotIntervalMinutes} min slots` : ''}`}
                          </div>
                        </div>
                        <button
                          onClick={() => removeCapacityRule(rule.id)}
                          className="text-slate-300 hover:text-red-500 hover:bg-red-50 p-2 rounded-lg transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                </div>
              </div>

              {/* Special Openings */}
              <div>
                <h4 className="font-bold text-sm text-slate-800 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-emerald-600" /> Special Opening Days
                </h4>
                <div className="bg-slate-50 border rounded-xl p-4 mb-4 space-y-3 shadow-inner">
                  <p className="text-[11px] text-slate-500">
                    Add one-off open days. These override weekly closed days and blockouts.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                    <input
                      type="date"
                      className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                      value={newSpecialOpening.date || ''}
                      onChange={(e) =>
                        setNewSpecialOpening((prev) => ({ ...prev, date: e.target.value }))
                      }
                    />
                    <input
                      type="time"
                      className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                      value={newSpecialOpening.start || '09:00'}
                      onChange={(e) =>
                        setNewSpecialOpening((prev) => ({ ...prev, start: e.target.value }))
                      }
                    />
                    <input
                      type="time"
                      className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                      value={newSpecialOpening.end || '17:00'}
                      onChange={(e) =>
                        setNewSpecialOpening((prev) => ({ ...prev, end: e.target.value }))
                      }
                    />
                    <input
                      type="number"
                      min={0}
                      max={50}
                      className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Cap (opt)"
                      value={newSpecialOpening.capacity == null ? '' : newSpecialOpening.capacity}
                      onChange={(e) =>
                        setNewSpecialOpening((prev) => ({
                          ...prev,
                          capacity: e.target.value === '' ? undefined : Number(e.target.value || 0)
                        }))
                      }
                    />
                    <button
                      onClick={() => {
                        void addSpecialOpening();
                      }}
                      className="bg-teal-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-teal-700 shadow-sm transition-all"
                    >
                      Add Day
                    </button>
                  </div>
                  <input
                    className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Label (optional)"
                    value={newSpecialOpening.label || ''}
                    onChange={(e) =>
                      setNewSpecialOpening((prev) => ({ ...prev, label: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                  {(venue.schedule?.specialOpenings || []).length === 0 && (
                    <p className="text-xs text-slate-400 italic text-center py-4 bg-white rounded-xl border border-dashed">
                      No special openings configured.
                    </p>
                  )}
                  {(venue.schedule?.specialOpenings || [])
                    .slice()
                    .sort((a, b) => a.date.localeCompare(b.date))
                    .map((opening) => (
                      <div
                        key={opening.id}
                        className="flex justify-between items-center bg-white border border-slate-200 p-3 rounded-xl shadow-sm group hover:border-teal-300 transition-all"
                      >
                        <div>
                          <div className="font-bold text-slate-800 text-sm">
                            {formatUkDate(opening.date, '—')}
                          </div>
                          <div className="text-[10px] font-bold text-slate-400 uppercase mt-0.5 tracking-tight">
                            {opening.start}-{opening.end} ·{' '}
                            {opening.capacity == null
                              ? 'Default capacity'
                              : `Cap ${opening.capacity}`}
                            {opening.label ? ` · ${opening.label}` : ''}
                          </div>
                        </div>
                        <button
                          onClick={() => removeSpecialOpening(opening.id)}
                          className="text-slate-300 hover:text-red-500 hover:bg-red-50 p-2 rounded-lg transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                </div>
              </div>

              {/* Blockout Rules */}
              <div>
                <h4 className="font-bold text-sm text-slate-800 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-orange-500" /> Blockout Rules & Holidays
                </h4>
                <div className="bg-slate-50 border rounded-xl p-4 mb-4 space-y-4 shadow-inner">
                  <div className="flex gap-2 text-[10px] font-bold uppercase tracking-wider">
                    {(['single', 'range', 'pattern'] as BlockoutType[]).map((type) => (
                      <button
                        key={type}
                        onClick={() => setNewRuleType(type)}
                        className={`px-3 py-1.5 rounded-full transition-all ${newRuleType === type ? 'bg-slate-800 text-white shadow-md' : 'bg-white border text-slate-500 hover:bg-white hover:text-slate-800'}`}
                      >
                        {type === 'single'
                          ? 'Single Date'
                          : type === 'range'
                            ? 'Date Range'
                            : 'Recurring'}
                      </button>
                    ))}
                  </div>
                  <div className="grid gap-3">
                    {newRuleType === 'single' && (
                      <input
                        type="date"
                        className="w-full border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                        value={newRuleData.date || ''}
                        onChange={(e) => setNewRuleData({ ...newRuleData, date: e.target.value })}
                      />
                    )}
                    {newRuleType === 'range' && (
                      <div className="flex gap-3">
                        <input
                          type="date"
                          className="border rounded-lg p-2 text-sm flex-1 bg-white outline-none focus:ring-2 focus:ring-teal-500"
                          placeholder="Start"
                          value={newRuleData.startDate || ''}
                          onChange={(e) =>
                            setNewRuleData({ ...newRuleData, startDate: e.target.value })
                          }
                        />
                        <input
                          type="date"
                          className="border rounded-lg p-2 text-sm flex-1 bg-white outline-none focus:ring-2 focus:ring-teal-500"
                          placeholder="End"
                          value={newRuleData.endDate || ''}
                          onChange={(e) =>
                            setNewRuleData({ ...newRuleData, endDate: e.target.value })
                          }
                        />
                      </div>
                    )}
                    {newRuleType === 'pattern' && (
                      <div className="space-y-3">
                        <div className="flex gap-3">
                          <select
                            className="border rounded-lg p-2 text-sm flex-1 bg-white outline-none focus:ring-2 focus:ring-teal-500"
                            value={newRuleData.dayOfWeek}
                            onChange={(e) =>
                              setNewRuleData({ ...newRuleData, dayOfWeek: Number(e.target.value) })
                            }
                          >
                            <option value="" disabled>
                              Select Day
                            </option>
                            {DAYS.map((d, i) => (
                              <option key={i} value={i}>
                                {d}
                              </option>
                            ))}
                          </select>
                          <select
                            className="border rounded-lg p-2 text-sm flex-1 bg-white outline-none focus:ring-2 focus:ring-teal-500"
                            value={newRuleData.frequency || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setNewRuleData({
                                ...newRuleData,
                                frequency: isVenueRuleFrequency(v) ? v : undefined
                              });
                            }}
                          >
                            <option value="" disabled>
                              Frequency
                            </option>
                            <option value="every">Every Week</option>
                            <option value="biweekly">Bi-Weekly</option>
                          </select>
                        </div>
                        {newRuleData.frequency === 'biweekly' && (
                          <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-200">
                            <label className="text-[10px] font-bold text-slate-400 uppercase">
                              Start From:
                            </label>
                            <input
                              type="date"
                              className="border-none p-0 text-xs font-medium outline-none"
                              value={newRuleData.startFrom || ''}
                              onChange={(e) =>
                                setNewRuleData({ ...newRuleData, startFrom: e.target.value })
                              }
                            />
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input
                        className="border rounded-lg p-2 text-sm flex-1 bg-white outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="Reason (e.g. Bank Holiday)"
                        value={newRuleData.reason || ''}
                        onChange={(e) => setNewRuleData({ ...newRuleData, reason: e.target.value })}
                      />
                      <button
                        onClick={() => {
                          void handleAddBlockoutRule();
                        }}
                        className="bg-teal-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-teal-700 shadow-sm transition-all"
                      >
                        Add Rule
                      </button>
                    </div>
                  </div>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                  {(venue.schedule?.blockouts || []).length === 0 && (
                    <p className="text-xs text-slate-400 italic text-center py-4 bg-white rounded-xl border border-dashed">
                      No blockout rules active.
                    </p>
                  )}
                  {(venue.schedule?.blockouts || []).map((rule) => (
                    <div
                      key={rule.id}
                      className="flex justify-between items-center bg-white border border-slate-200 p-3 rounded-xl shadow-sm group hover:border-teal-300 transition-all"
                    >
                      <div>
                        <div className="font-bold text-slate-800 text-sm">{rule.reason}</div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase mt-0.5 tracking-tight">
                          {rule.type === 'single' && `Date: ${formatUkDate(rule.date, '—')}`}
                          {rule.type === 'range' &&
                            `Range: ${formatUkDate(rule.startDate, '—')} - ${formatUkDate(rule.endDate, '—')}`}
                          {rule.type === 'pattern' &&
                            `${rule.frequency === 'biweekly' ? 'Every 2nd' : 'Every'} ${DAYS[rule.dayOfWeek!]} ${rule.frequency === 'biweekly' ? '(from ' + formatUkDate(rule.startFrom, '—') + ')' : ''}`}
                        </div>
                      </div>
                      <button
                        onClick={() => removeBlockoutRule(rule.id)}
                        className="text-slate-300 hover:text-red-500 hover:bg-red-50 p-2 rounded-lg transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-slate-50 rounded-b-xl flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-5 py-2 text-slate-600 font-bold hover:bg-slate-100 rounded-lg transition-all"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              void handleSave();
            }}
            disabled={isSaving}
            className="bg-slate-900 text-white px-8 py-2 rounded-lg font-bold hover:bg-slate-800 shadow-lg active:scale-95 transition-all"
          >
            {isSaving ? 'Saving...' : 'Save Venue'}
          </button>
        </div>
      </div>
    </div>
  );
};
